import _ from "lodash";
/** External */
import React, { useState } from "react";
import { connect } from "react-redux";
import { ToggleSwitch, Button, Badge, Tooltip, Tag } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";

/** Internal */
import { graphql, compose } from "lib/apolloClient";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import {
  CURRICULUM_PROGRAM,
  AI_TUTOR,
  AI_TUTOR_MODALITIES,
} from "../../../modules/OrgAddOnContants";
import { updateAddOnStatus } from "../../../modules/OrgAddOnHelpers";
import { getInternalToolGenericDataVariables } from "AddOnManager/modules/AddOnManagerUtils";

/** Relative */
import classes from "./CurriculumProgramList.scss";
import StaffList from "./../../ToddleAi/StaffList";

const CurriculumProgramList = ({
  orgId,
  statusMapping,
  updateAddOnStatus,
  curriculumProgramsOfOrg,
  getInternalToolGenericDataVariables,
  onSuccessAddOnConfigChange,
  isAiTutorModalityEnabled,
  isAiTutorModalityEnabledOnMount,
}) => {
  const { t } = useI18n(["common", "ai"]);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [selectedCurriculumId, setSelectedCurriculumId] = useState(null);
  const [isStaffListModalOpen, setIsStaffListModalOpen] = useState(false);
  const [
    selectedCurriculumEnabledUsers,
    setSelectedCurriculumEnabledUsers,
  ] = useState([]);

  const isAiTutorV2Enabled =
    isAiTutorModalityEnabledOnMount || isAiTutorModalityEnabled;

  return (
    <div>
      {!_.isEmpty(curriculumProgramsOfOrg) && (
        <div className={classes.parentContainer}>
          <div className={classes.listContainer}>
            {_.map(curriculumProgramsOfOrg, curriculumProgram => {
              const { id, label, acronym } = curriculumProgram;

              const enabledForCurrentCurriculum = _.get(
                statusMapping,
                `${id}.status`,
                false
              );

              const enabledUsers = _.get(
                statusMapping,
                `${id}.enabledUsers`,
                []
              );

              const handleCurriculumLevelToggle = async () => {
                setFullScreenLoader(true);
                await updateAddOnStatus({
                  orgId,
                  curriculumId: id,
                  addOnType: AI_TUTOR,
                  moduleId: AI_TUTOR,
                  updateOn: CURRICULUM_PROGRAM,
                  newStatus: !enabledForCurrentCurriculum,
                  queryVariables: getInternalToolGenericDataVariables,
                });
                if (!_.isNil(onSuccessAddOnConfigChange))
                  onSuccessAddOnConfigChange();
                setFullScreenLoader(false);
              };

              const badgeVariant = enabledForCurrentCurriculum
                ? "subtle"
                : "notifications";

              const tooltipText = enabledForCurrentCurriculum
                ? t(
                    "ai:this_feature_is_enabled_for_this_curriculum_it_will_be_enabled_for_all_the_staff"
                  )
                : t("ai:select_individial_staff_members");

              return (
                <div key={id} className={classes.curriculumProgramItem}>
                  <div>
                    <div className={classes.labelRow}>
                      <div>{label}</div>
                      {enabledForCurrentCurriculum && (
                        <Tag dsVersion="2.0" size="small" color="yellow">
                          {isAiTutorV2Enabled ? "V2" : "V1"}
                        </Tag>
                      )}
                    </div>
                    <div
                      className={classes.acronymTypeString}
                    >{`${acronym} - (${id})`}</div>
                  </div>
                  <div className={classes.actionItems}>
                    <div className={classes.curriculumLevel}>
                      <div className={classes.curriculumWideText}>
                        {t("ai:curriculum_wide")}
                      </div>
                      <ToggleSwitch
                        onChange={handleCurriculumLevelToggle}
                        checked={enabledForCurrentCurriculum}
                      />
                    </div>
                    <div className={classes.orSeperator}>
                      {t("common:or_lowercase")}
                    </div>
                    <Badge
                      variant={badgeVariant}
                      value={_.size(enabledUsers)}
                      placement="topRight"
                    >
                      <Tooltip tooltip={tooltipText}>
                        <Button
                          disabled={enabledForCurrentCurriculum}
                          _legacy_variant="outlined-subtle"
                          variant="neutral"
                          type="outlined"
                          onClick={() => {
                            setSelectedCurriculumId(id);
                            setSelectedCurriculumEnabledUsers(enabledUsers);
                            setIsStaffListModalOpen(true);
                          }}
                        >
                          {t("common:select_staff")}
                        </Button>
                      </Tooltip>
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {isStaffListModalOpen ? (
        <StaffList
          orgId={orgId}
          setFullScreenLoader={setFullScreenLoader}
          selectedCurriculumId={selectedCurriculumId}
          isStaffListModalOpen={isStaffListModalOpen}
          setIsStaffListModalOpen={setIsStaffListModalOpen}
          selectedCurriculumEnabledUsers={selectedCurriculumEnabledUsers}
          getInternalToolGenericDataVariables={
            getInternalToolGenericDataVariables
          }
          feature={{ id: AI_TUTOR }}
          addOnType={AI_TUTOR}
          onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
        />
      ) : null}
      {fullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapActionCreators = {
  updateAddOnStatus,
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const getInternalToolGenericDataVariablesForAiTutor = {
    input: {
      apiType: "ADD_ON_MANAGER",
      params: {
        organizationId: orgId,
        addOnType: AI_TUTOR,
      },
    },
  };

  const aiTutorModalityVariables = getInternalToolGenericDataVariables({
    orgId,
    addOnType: AI_TUTOR_MODALITIES,
  });

  return {
    isData: true,
    isLoading: false,
    organizationId: orgId,
    getInternalToolGenericDataVariables: getInternalToolGenericDataVariablesForAiTutor,
    aiTutorModalityVariables,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    options: ({ getInternalToolGenericDataVariables }) => ({
      fetchPolicy: "network-only",
      variables: getInternalToolGenericDataVariables,
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const queryData = _.get(
        getInternalToolGenericData,
        "platform.internalToolsGenericQuery",
        {}
      );

      const curriculumPrograms = _.get(
        queryData,
        "data.curriculumPrograms",
        []
      );

      const statusMapping = _.reduce(
        curriculumPrograms,
        (result, curriculumProgram) => {
          const { id, status = false, enabledUsers = [] } = curriculumProgram;
          return {
            ...result,
            [id]: {
              status,
              enabledUsers,
            },
          };
        },
        {}
      );

      const isAiTutorModalityEnabled = _.get(
        queryData,
        "data.isAiTutorModalitiesEnabled",
        false
      );

      return {
        statusMapping,
        isAiTutorModalityEnabled,
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          _.includes([1, 2], getInternalToolGenericData["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  graphql(getInternalToolGenericDataQuery, {
    name: "getAiTutorModalityData",
    options: ({ aiTutorModalityVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: aiTutorModalityVariables,
    }),
    props: ({ getAiTutorModalityData, ownProps: { isData, isLoading } }) => {
      const modalityData = _.get(
        getAiTutorModalityData,
        "platform.internalToolsGenericQuery.data",
        {}
      );

      const withWhiteboard = _.get(modalityData, "withWhiteboard", false);
      const withoutWhiteboard = _.get(modalityData, "withoutWhiteboard", false);

      return {
        isAiTutorModalityEnabledOnMount: withWhiteboard || withoutWhiteboard,
        isData: !_.isEmpty(modalityData) && isData,
        isLoading:
          _.includes([1, 2], getAiTutorModalityData["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  withLoader
)(CurriculumProgramList);
